interface RateMeterProps {
  label: string
  /** Current rate, as a percent (e.g. 4.2 means 4.2%). */
  value: number
  /** The breaker's threshold, as a percent. */
  max: number
  /**
   * Which side of the threshold is the failure (ORB-706).
   *
   * `ceiling` — bounces and complaints: over the limit is bad, and the bar fills toward it.
   * `floor` — the canary delivery gate: BELOW the limit is bad, so the bar fills toward 100% and
   * empties toward failure. Rendering a floor as a ceiling reads exactly backwards, which is why this
   * is a direction rather than the caller passing an inverted number.
   */
  direction?: 'ceiling' | 'floor'
  /** What the rate was measured over, e.g. "measured on the 200-email test batch". */
  sample?: string
  /**
   * The blast has finished. Late bounces routinely push a completed send's cumulative rate over the
   * line hours after the breaker stopped watching, and red would claim something is still about to
   * stop — nothing is. Over-limit reads amber and says so instead.
   */
  isFinal?: boolean
  /** The gate does not apply in this state (e.g. the delivery floor once the drain is under way). */
  notApplicable?: string
}

/**
 * A breaker-rate readout: the current rate against its threshold, with a bar that fills toward the
 * healthy end and turns red once the gate would trip. Used for hard-bounce, complaint and delivery.
 */
export function RateMeter({
  label,
  value,
  max,
  direction = 'ceiling',
  sample,
  isFinal = false,
  notApplicable,
}: RateMeterProps) {
  if (notApplicable) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
          <span className="text-[13px] font-medium text-slate-400 dark:text-slate-500">not checked here</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800" />
        <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">{notApplicable}</div>
      </div>
    )
  }

  // Strictly outside the threshold, matching EmailBlastEngine.EvaluateBreaker — a rate exactly AT the
  // limit passes there, and a meter that reddens a step earlier tells an operator a send is stopping
  // when it is not. Very reachable on canary-sized denominators (1 bounce in 50 is exactly 2.00%).
  const breached = max > 0 && (direction === 'ceiling' ? value > max : value < max)
  // Ceiling: fill toward the limit, so a healthy rate is a short bar. Floor: fill toward 100%, so a
  // healthy rate is a full bar and the meter drains as it approaches failure.
  const fillPercent =
    direction === 'ceiling'
      ? max > 0
        ? Math.min((value / max) * 100, 100)
        : 0
      : Math.min(Math.max(value, 0), 100)

  const tone = !breached ? 'ok' : isFinal ? 'late' : 'bad'
  const valueClass =
    tone === 'bad'
      ? 'text-red-600 dark:text-red-400'
      : tone === 'late'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-slate-900 dark:text-white'
  const barClass = tone === 'bad' ? 'bg-red-500' : tone === 'late' ? 'bg-amber-500' : 'bg-emerald-500'

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value.toFixed(2)}%</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barClass}`} style={{ width: `${fillPercent}%` }} />
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-400 dark:text-slate-500">
        <span>
          {direction === 'ceiling' ? 'Limit' : 'Must stay above'} {max.toFixed(2)}%
        </span>
        {breached && (
          <span className={`font-medium ${tone === 'late' ? 'text-amber-500 dark:text-amber-400' : 'text-red-500 dark:text-red-400'}`}>
            {/* Direction decides the wording before tone does: a floor is never "above" anything, which
                is the inversion the direction prop exists to prevent. */}
            {direction === 'floor' ? 'Below the floor' : tone === 'late' ? 'Above the limit' : 'Over limit'}
          </span>
        )}
      </div>
      {sample && <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{sample}</div>}
    </div>
  )
}
